import { FileText } from 'lucide-react';
import { type KeyboardEvent, useRef, useState } from 'react';

import advisorkhojLogo from '@/assets/email-logo.webp';
import { Spinner } from '@/components/ui';
import { useTcDocuments } from '@/hooks';
import type { TcType } from '@/services/tc.service';
import cn from '@/utils/cn';

const TAB_LABEL: Record<TcType, string> = {
  ALL: 'All Products',
  MFTOOLS: 'MFTools',
  API: 'APIs',
};

const TAB_ORDER: TcType[] = ['ALL', 'MFTOOLS', 'API'];

const tabId = (type: TcType) => `tc-tab-${type}`;
const PANEL_ID = 'tc-tabpanel';

const TcDocumentsPage = () => {
  const { data: documents, isLoading, isError } = useTcDocuments();
  const [active, setActive] = useState<TcType>('ALL');

  const activeDoc = documents?.find((d) => d.type === active);
  const tabRefs = useRef<Partial<Record<TcType, HTMLButtonElement | null>>>({});

  // Roving tabindex: only the selected tab is in the tab sequence, so the
  // arrow / Home / End keys have to move selection and focus themselves -
  // without this the other tabs are unreachable from the keyboard.
  const onTabKeyDown = (e: KeyboardEvent<HTMLButtonElement>) => {
    const count = TAB_ORDER.length;
    const current = TAB_ORDER.indexOf(active);
    let nextIndex = -1;
    if (e.key === 'ArrowRight') nextIndex = (current + 1) % count;
    if (e.key === 'ArrowLeft') nextIndex = (current - 1 + count) % count;
    if (e.key === 'Home') nextIndex = 0;
    if (e.key === 'End') nextIndex = count - 1;
    if (nextIndex === -1) return;
    const nextType = TAB_ORDER[nextIndex];
    e.preventDefault();
    setActive(nextType);
    tabRefs.current[nextType]?.focus();
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <FileText className="h-5 w-5 shrink-0 text-brand" />
        <h1 className="text-lg font-bold">Terms &amp; Conditions</h1>
        <span className="text-xs text-muted-foreground">
          Preview of the documents shared with leads
        </span>
      </div>

      <div role="tablist" aria-label="Document type" className="flex flex-wrap gap-1.5">
        {TAB_ORDER.map((type) => (
          <button
            key={type}
            type="button"
            role="tab"
            id={tabId(type)}
            aria-selected={active === type}
            aria-controls={PANEL_ID}
            tabIndex={active === type ? 0 : -1}
            ref={(el) => {
              tabRefs.current[type] = el;
            }}
            onClick={() => setActive(type)}
            onKeyDown={onTabKeyDown}
            className={cn(
              'min-h-11 rounded-lg border px-3 py-1.5 text-sm font-medium focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring sm:min-h-0',
              active === type
                ? 'border-brand bg-brand-tint text-brand'
                : 'border-border bg-card text-foreground hover:bg-secondary/40',
            )}
          >
            {TAB_LABEL[type]}
          </button>
        ))}
      </div>

      <div
        role="tabpanel"
        id={PANEL_ID}
        aria-labelledby={tabId(active)}
        tabIndex={0}
        className="mx-auto w-full max-w-4xl rounded-xl border border-border bg-card p-4 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring sm:p-6"
      >
        {!isLoading && !isError && activeDoc && (
          <div className="mb-4 flex justify-center">
            <img src={advisorkhojLogo} alt="AdvisorKhoj" className="h-10 object-contain" />
          </div>
        )}
        {isLoading && (
          <div className="flex justify-center py-10">
            <Spinner />
          </div>
        )}
        {isError && !isLoading && (
          <p className="text-sm text-danger">
            Could not load the Terms &amp; Conditions documents.
          </p>
        )}
        {!isLoading && !isError && activeDoc && (
          <div
            className="tc-doc overflow-x-auto"
            // eslint-disable-next-line react/no-danger -- trusted server-authored T&C content
            dangerouslySetInnerHTML={{ __html: activeDoc.html }}
          />
        )}
      </div>
    </div>
  );
};

export default TcDocumentsPage;
